import { createIdempotencyKey } from "@lafayette/api-client";
import type { StaffCatalogItemDetail } from "@lafayette/schemas";
import { useDataProvider } from "@refinedev/core";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useRef, useState } from "react";

import {
  catalogResources,
  type ItemCommandInputs,
  type ItemCommandMeta,
  type ItemCommandName,
} from "./data-provider";
import { type CatalogFailure, describeCatalogError } from "./errors";
import { catalogKeys, useItemFetcher } from "./queries";

// Runs an item command (W2 to W18) through the data provider's `custom` call and keeps the cached detail
// current. Three rules live here:
//   1. Idempotency. One user action has one key. After a failure where the same command may run again
//      (no answer, a 503) the next identical attempt reuses the key, so the server runs it once.
//   2. Version. Every command carries `expectedVersion`. On a 409 the item is read again; a different
//      `version` means somebody else changed it (`changedElsewhere`), the same `version` means a rule
//      refused the change.
//   3. The cache. A success writes the detail the server returned into the item's cache entry.

export type ConflictInfo = Readonly<{
  /** The item's `version` differs from the one the command carried. */
  changedElsewhere: boolean;
  sentVersion: number;
  latestVersion: number | null;
}>;

export type CommandOutcome =
  | Readonly<{ ok: true; detail: StaffCatalogItemDetail }>
  | Readonly<{ ok: false; failure: CatalogFailure; conflict: ConflictInfo | null }>;

export function useCatalogCommand(itemId: string) {
  const getDataProvider = useDataProvider();
  const queryClient = useQueryClient();
  const fetchItem = useItemFetcher();
  const pendingKey = useRef<{ signature: string; key: string } | null>(null);
  const [pendingCount, setPendingCount] = useState(0);

  /** Reads the item again after a 409 and tells a change by someone else from a rule refusal. */
  const readConflict = useCallback(
    async (sentVersion: number): Promise<ConflictInfo> => {
      try {
        const latest = await fetchItem(itemId);
        queryClient.setQueryData(catalogKeys.item(itemId), latest);
        return {
          changedElsewhere: latest.item.version !== sentVersion,
          sentVersion,
          latestVersion: latest.item.version,
        };
      } catch {
        return { changedElsewhere: false, sentVersion, latestVersion: null };
      }
    },
    [fetchItem, itemId, queryClient],
  );

  const run = useCallback(
    async <K extends ItemCommandName>(
      command: K,
      input: ItemCommandInputs[K],
    ): Promise<CommandOutcome> => {
      const signature = JSON.stringify([command, itemId, input]);
      const key =
        pendingKey.current?.signature === signature
          ? pendingKey.current.key
          : createIdempotencyKey();
      pendingKey.current = { signature, key };
      const meta: ItemCommandMeta = { command, itemId, idempotencyKey: key };

      setPendingCount((count) => count + 1);
      try {
        const response = await getDataProvider().custom?.({
          url: `${catalogResources.items}/${itemId}/${command}`,
          method: "post",
          payload: input,
          meta,
        });
        const detail = response?.data as StaffCatalogItemDetail;
        pendingKey.current = null;
        queryClient.setQueryData(catalogKeys.item(itemId), detail);
        return { ok: true, detail };
      } catch (error) {
        const failure = describeCatalogError(error);
        if (!failure.retryable) pendingKey.current = null;
        if (failure.kind !== "conflict") return { ok: false, failure, conflict: null };
        return { ok: false, failure, conflict: await readConflict(input.expectedVersion) };
      } finally {
        setPendingCount((count) => count - 1);
      }
    },
    [getDataProvider, itemId, queryClient, readConflict],
  );

  return { run, pending: pendingCount > 0 };
}

export type SaveMessage = Readonly<{
  tone: "success" | "error" | "conflict";
  text: string;
}>;

/** The sentence under a section after a command. A conflict says which of the two it was. */
export function describeOutcome(outcome: CommandOutcome, successText: string): SaveMessage {
  if (outcome.ok) return { tone: "success", text: successText };
  const { conflict, failure } = outcome;
  if (conflict?.changedElsewhere) {
    return {
      tone: "conflict",
      text: `This item was changed elsewhere (version ${conflict.sentVersion} is now version ${conflict.latestVersion}). Your edits are still in the form. Review them and save again.`,
    };
  }
  if (conflict !== null) {
    return {
      tone: "error",
      text: "The server refused this change. If the item is published, the change may leave it unable to be published. Check the readiness list at the top of the page.",
    };
  }
  return { tone: "error", text: failure.message };
}
